import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { addDriver } from "../../../actions/admin/driver";
import Alert from "../../layout/Alert";
import AdminPage from "../_components/AdminPage";
import {
  AdminFormCard,
  AdminFormFullRow,
  AdminTextField,
  AdminTextAreaField,
  AdminToggleField,
  AdminRadioField,
  AdminBackLink,
  AdminFormButton,
  AdminFormActions,
} from "../_components/AdminForm";
import ConfirmationModal from "../components/ConfirmationModal";
import SuccessModal from "../components/SuccessModal";
import { ValueList, summarizeFields, asText, asOnOff } from "../components/ChangeSummary";
import useSaveConfirmation from "../components/useSaveConfirmation";

export const DRIVER_LIST = "/admin/driverlist";

export const CONTRACT_OPTIONS = [
  { value: true, label: "Full time" },
  { value: false, label: "Part time" },
];

// The fields these forms write, in the order they show them.
export const DRIVER_FIELDS = [
  { name: "name", label: "Name", format: asText },
  { name: "email", label: "Email", format: asText },
  { name: "phone", label: "Phone number", format: asText },
  { name: "contract", label: "Contract", format: asText },
  {
    name: "isfulltime",
    label: "Employment",
    format: (value) => (value ? "Full time" : "Part time"),
  },
  { name: "isactive", label: "Active", format: asOnOff },
];

const AddDriver = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [formData, setFormData] = useState({
    _id: null,
    contract: "",
    name: "",
    email: "",
    phone: "",
    isfulltime: true,
    isactive: false,
  });
  const { contract, name, email, phone, isfulltime, isactive } = formData;

  const onChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const save = useSaveConfirmation({
    save: () => dispatch(addDriver(formData, null)),
    onDismiss: () => history.push(DRIVER_LIST),
  });

  const onSubmit = (e) => {
    e.preventDefault();
    save.request(summarizeFields(DRIVER_FIELDS, formData));
  };

  return (
    <AdminPage title="Add Driver" actions={<AdminBackLink to={DRIVER_LIST} />}>
      <form onSubmit={onSubmit} className="tw:max-w-[720px]">
        <AdminFormCard title="Driver status">
          <AdminToggleField
            name="isactive"
            label="Driver is active"
            description="Only active drivers can be assigned to a booking."
            checked={isactive}
            onChange={() => setFormData({ ...formData, isactive: !isactive })}
          />

          <AdminRadioField
            label="Driver is"
            name="isfulltime"
            value={isfulltime}
            options={CONTRACT_OPTIONS}
            onChange={(value) => setFormData({ ...formData, isfulltime: value })}
          />
        </AdminFormCard>

        <AdminFormCard title="Driver details" columns={2}>
          <AdminTextField
            name="name"
            label="Name"
            placeholder="Enter name"
            value={name}
            onChange={onChange}
            required
          />

          <AdminTextField
            name="email"
            label="Email"
            type="email"
            placeholder="Enter email"
            value={email}
            onChange={onChange}
          />

          <AdminTextField
            name="phone"
            label="Phone number"
            type="tel"
            placeholder="Enter phone number"
            value={phone}
            onChange={onChange}
          />

          <AdminFormFullRow>
            <AdminTextAreaField
              name="contract"
              label="Contract"
              placeholder="Enter contract"
              value={contract}
              onChange={onChange}
            />
          </AdminFormFullRow>
        </AdminFormCard>

        <AdminFormActions>
          <AdminFormButton type="submit">Add Driver</AdminFormButton>
        </AdminFormActions>
      </form>

      <Alert />

      <ConfirmationModal
        show={!!save.summary}
        title="Create this driver?"
        description="They will be added to the roster and can be assigned to bookings straight away."
        confirmLabel="Add Driver"
        busyLabel="Creating..."
        isBusy={save.isSaving}
        error={save.error}
        onCancel={save.cancel}
        onConfirm={save.confirm}
      >
        <ValueList rows={save.summary || []} />
      </ConfirmationModal>

      <SuccessModal
        show={!!save.saved}
        title="Driver created"
        description="They are on the roster now. Closing this returns you to the driver list."
        onClose={save.dismiss}
      >
        <ValueList rows={save.saved || []} />
      </SuccessModal>
    </AdminPage>
  );
};

export default AddDriver;
